/**
 * Ledger Test Screen — Hidden test route for manual ledger entries
 *
 * Loads order's manual entries, displays selectable list, and opens
 * LedgerEntryEditorModal for testing configuration and entry editing.
 *
 * Route: /orders/details/:id/ledger-test (hidden, no menu links)
 */

import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  FormGroup, ListItemButton, Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useQuery } from 'react-query';
import gqlRequest from 'graphql/generic';
import { AccountingType } from 'v2-types/order';
import { OrderLedgerKinds } from 'components/ledger-entry-editor/contracts';
import { LedgerEntryEditorModal } from 'components/ledger-entry-editor/LedgerEntryEditorModal';
import { LedgerEditorConfig, EntryFieldKey, SettingsFieldKey } from 'components/ledger-entry-editor/config';
import { EntryDraft } from 'components/ledger-entry-editor/engine/dirty';
import { formatCurrency } from 'components/ledger-entry-editor/utils/format';
import { formatDateDisplay, parseLocalDate } from 'components/ledger-entry-editor/utils/date';
import { getAccountingTranslation } from 'components/ledger-entry-editor/utils/translation';
import { sanitizeDescriptionRemovePayee } from 'components/ledger-entry-editor/utils/sanitize';
import { OutlinedButton } from 'components/OutlinedButton';
import { ContainedButton } from 'components/ContainedButton';
import { normalizeLedgerError } from 'components/ledger-entry-editor/engine/errors';
import useAlert from 'utils/alert';

const normalizeDateForDisplay = (value: unknown): string => {
  if (!value || typeof value !== 'string') return '';
  const usResult = parseLocalDate(value);
  if (usResult.valid && usResult.parts) {
    return formatDateDisplay(usResult.parts.month, usResult.parts.day, usResult.parts.year);
  }
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return formatDateDisplay(Number(isoMatch[2]), Number(isoMatch[3]), Number(isoMatch[1]));
  }
  return '';
};

// Field option constants for configuration
const ENTRY_FIELD_OPTIONS: EntryFieldKey[] = [
  'amount',
  'code',
  'description',
  'kind',
  'entryDate',
  'letter',
  'number',
  'months',
  'months_advanced',
];
const SETTINGS_FIELD_OPTIONS: SettingsFieldKey[] = ['posted'];

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: theme.spacing(2),
    maxWidth: 1000,
    margin: '0 auto',
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  configPaper: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  configTitle: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
  },
  configSection: {
    marginBottom: theme.spacing(1.5),
  },
  configSectionTitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
  },
  formGroup: {
    display: 'contents',
  },
  formGroupGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  formControlLabel: {
    margin: 0,
    '& .MuiCheckbox-root': {
      padding: theme.spacing(0.5),
    },
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
    },
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  entryList: {
    marginTop: theme.spacing(2),
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  errorContainer: {
    marginBottom: theme.spacing(2),
  },
}));

const queryGetOrderAccounting = `
  query GetOrder($id: ID!) {
    getOrder(_id: $id) {
      accounting {
        _id
        amount
        amount_calculated
        amount_override
        code
        description
        entry_date
        kind
        letter
        number
        months
        months_advanced
        posted
        involved_parties {
          _id
          amount
          percent
          kind
          name
        }
      }
    }
  }
`;

interface GetOrderAccountingReturn {
  getOrder: {
    accounting: AccountingType[];
  };
}

const LedgerTestScreen: React.FC = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const alert = useAlert();
  const [selectedEntry, setSelectedEntry] = useState<AccountingType | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Configuration state — track selected fields (Entry only; Association/Party/Link removed per scope reduction)
  const [entryFields, setEntryFields] = useState<Set<EntryFieldKey>>(
    new Set<EntryFieldKey>(['amount', 'description', 'entryDate', 'code'])
  );
  const [settingsFields, setSettingsFields] = useState<Set<SettingsFieldKey>>(
    new Set<SettingsFieldKey>(['posted'])
  );

  // Flags state
  const [enablePosted, setEnablePosted] = useState(true);
  const [enableDelete, setEnableDelete] = useState(false);
  const [requireDescription, setRequireDescription] = useState(true);

  // Load order accounting entries
  const { data, isLoading, error, refetch } = useQuery(
    ['ledger-test', id],
    async () => {
      const result = await gqlRequest<GetOrderAccountingReturn, { id: string }>(queryGetOrderAccounting, { id });
      return result.getOrder.accounting || [];
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (err) => alert(normalizeLedgerError(err), 'error'),
    }
  );

  // Filter manual entries: kind must be manual, letter must be non-empty string, number must be finite
  const manualEntries = useMemo(() => {
    if (!data) return [];
    return data.filter((entry: AccountingType) => {
      if (entry.kind !== OrderLedgerKinds.manual) return false;
      const letterValid = entry.letter.trim().length > 0;
      // Backend may deliver `number` as a string (see test data comment)
      const numberValue = entry.number as unknown as string | number;
      const numberValid = typeof numberValue === 'string' && numberValue.trim().length > 0;
      return letterValid && numberValid;
    });
  }, [data]);

  // Toggle field selections
  const toggleEntryField = (field: EntryFieldKey) => {
    const newSet = new Set(entryFields);
    if (newSet.has(field)) newSet.delete(field);
    else newSet.add(field);
    setEntryFields(newSet);
  };

  const toggleSettingsField = (field: SettingsFieldKey) => {
    const newSet = new Set(settingsFields);
    if (newSet.has(field)) newSet.delete(field);
    else newSet.add(field);
    setSettingsFields(newSet);
  };

  const selectAllFields = () => {
    setEntryFields(new Set<EntryFieldKey>(ENTRY_FIELD_OPTIONS));
    setSettingsFields(new Set<SettingsFieldKey>(SETTINGS_FIELD_OPTIONS));
  };

  // Clear all fields
  const clearAllFields = () => {
    setEntryFields(new Set<EntryFieldKey>());
    setSettingsFields(new Set<SettingsFieldKey>());
  };

  // Handle entry selection
  const handleSelectEntry = (entry: AccountingType) => {
    setSelectedEntry(entry);
    setModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedEntry(null);
    // Refresh query to remove deleted entries and update UI
    refetch();
  };

  // Derive config from state — memoized to avoid infinite loop
  // Note: Association, Party, and Link fields are intentionally excluded per scope reduction
  const testConfig = useMemo<LedgerEditorConfig>(() => ({
    entryFields: Array.from(entryFields),
    settingsFields: Array.from(settingsFields),
    requireDescription,
    enablePosted,
    enableDelete,
  }), [entryFields, settingsFields, requireDescription, enablePosted, enableDelete]);

  // Convert entry to draft for modal
  const entryDraft: EntryDraft | null = selectedEntry
    ? {
      orderId: id,
      entryId: selectedEntry._id,
      amount: selectedEntry.amount,
      amount_override: selectedEntry.amount_override,
      code: selectedEntry.code,
      description: selectedEntry.description,
      entryDate: selectedEntry.entry_date,
      kind: selectedEntry.kind,
      letter: selectedEntry.letter,
      number: selectedEntry.number,
      months: selectedEntry.months,
      months_advanced: selectedEntry.months_advanced,
      posted: selectedEntry.posted || false,
      involved_parties: selectedEntry.involved_parties || [],
    }
    : null;

  if (isLoading) {
    return (
      <Box className={classes.container}>
        <Paper className={classes.paper}>
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.container}>
        <Paper className={classes.paper}>
          <Alert severity="error" className={classes.errorContainer}>
            <Typography variant="body2">
              {normalizeLedgerError(error)}
            </Typography>
          </Alert>
          <ContainedButton text={getAccountingTranslation('common:retry', 'Retry')} onClick={() => refetch()} />
        </Paper>
      </Box>
    );
  }

  if (manualEntries.length === 0) {
    return (
      <Box className={classes.container}>
        <Paper className={classes.paper}>
          <Box className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>
              {getAccountingTranslation('no-manual-entries', 'No Manual Entries')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {getAccountingTranslation('no-manual-entries-description', 'There are no manual entries in this order.')}
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Configuration Selector */}
      <Paper className={classes.configPaper}>
        <Typography variant="h6" className={classes.configTitle}>
          {getAccountingTranslation('config-selector', 'Configuration')}
        </Typography>

        {/* Helper buttons */}
        <Box className={classes.buttonGroup}>
          <OutlinedButton
            size="small"
            buttonColor="secondary"
            text={getAccountingTranslation('select-all', 'Select All')}
            onClick={selectAllFields}
          />
          <OutlinedButton
            size="small"
            buttonColor="primary"
            text={getAccountingTranslation('clear-all', 'Clear All')}
            onClick={clearAllFields}
          />
        </Box>

        {/* Entry Fields */}
        <Box className={classes.configSection}>
          <Typography variant="overline" className={classes.configSectionTitle}>
            {getAccountingTranslation('tabs:entry', 'Entry')}
          </Typography>
          <Box className={classes.formGroupGrid}>
            <FormGroup className={classes.formGroup}>
              {ENTRY_FIELD_OPTIONS.map((field) => (
                <FormControlLabel
                  key={field}
                  className={classes.formControlLabel}
                  control={
                    <Checkbox
                      checked={entryFields.has(field)}
                      onChange={() => toggleEntryField(field)}
                      color="primary"
                    />
                   }
                  label={getAccountingTranslation(`fields:${field}`, field)}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>

        {/* Settings Fields */}
        <Box className={classes.configSection}>
          <Typography variant="overline" className={classes.configSectionTitle}>
            {getAccountingTranslation('tabs:settings', 'Settings')}
          </Typography>
          <Box className={classes.formGroupGrid}>
            <FormGroup className={classes.formGroup}>
              {SETTINGS_FIELD_OPTIONS.map((field) => (
                <FormControlLabel
                  key={field}
                  className={classes.formControlLabel}
                  control={
                    <Checkbox
                      checked={settingsFields.has(field)}
                      onChange={() => toggleSettingsField(field)}
                      color="primary"
                    />
                   }
                  label={getAccountingTranslation(`fields:${field}`, field)}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>

        {/* Flags Section */}
        <Box className={classes.configSection}>
          <Typography variant="overline" className={classes.configSectionTitle}>
            {getAccountingTranslation('flags', 'Flags')}
          </Typography>
          <Box className={classes.formGroupGrid}>
            <FormGroup className={classes.formGroup}>
              <FormControlLabel
                className={classes.formControlLabel}
                control={
                  <Checkbox
                    checked={requireDescription}
                    onChange={(e) => setRequireDescription(e.target.checked)}
                    color="primary"
                  />
                 }
                label={getAccountingTranslation('require-description', 'Require Description')}
              />
              <FormControlLabel
                className={classes.formControlLabel}
                control={
                  <Checkbox
                    checked={enablePosted}
                    onChange={(e) => setEnablePosted(e.target.checked)}
                    color="primary"
                  />
                 }
                label={getAccountingTranslation('enable-posted', 'Enable Posted')}
              />
              <FormControlLabel
                className={classes.formControlLabel}
                control={
                  <Checkbox
                    checked={enableDelete}
                    onChange={(e) => setEnableDelete(e.target.checked)}
                    color="primary"
                  />
                 }
                label={getAccountingTranslation('enable-delete', 'Enable Delete')}
              />
            </FormGroup>
          </Box>
        </Box>
      </Paper>

      {/* Manual Entries List */}
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          {getAccountingTranslation('manual-entries-test', 'Manual Entries (Test)')}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {getAccountingTranslation('manual-entries-test-description', 'Select an entry to open the editor modal.')}
        </Typography>

        <List className={classes.entryList}>
          {manualEntries.map((entry) => {
            const sanitizedDesc = sanitizeDescriptionRemovePayee(entry.description, entry.involved_parties);
            return (
              <ListItemButton
                key={entry._id}
                onClick={() => handleSelectEntry(entry)}
                className={classes.listItem}
              >
                <ListItemText
                  primary={sanitizedDesc || getAccountingTranslation('common:no-description', 'No description')}
                  secondary={`${formatCurrency(entry.amount)} | ${normalizeDateForDisplay(entry.entry_date)}`}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Paper>

      {/* Ledger Entry Editor Modal */}
      {entryDraft && (
      <LedgerEntryEditorModal
        open={modalOpen}
        initialEntry={entryDraft}
        config={testConfig}
        onClose={handleModalClose}
        enableDelete={enableDelete}
      />
      )}
    </Box>
  );
};

export default LedgerTestScreen;
